/**
 * useAudioDevices Hook
 *
 * Manages audio device enumeration, selection, and persistence.
 * Handles platform-specific logic for Windows, macOS, and mobile devices.
 *
 * @module hooks/useAudioDevices
 */
/* global HTMLAudioElement, AudioContext, MediaStream */

import { useCallback, useEffect, useRef, useState } from 'react';
import { track as breadcrumb } from '@/lib/telemetry/breadcrumb';
import { generateTestToneWav, supportsSinkId } from '@/utils/audio';
import {
  filterMicrophones,
  filterSpeakers,
  findDefaultSpeaker,
  getSpeakerLabel,
  getStoredDeviceId,
  isProblemBluetoothDevice,
  STORAGE_KEYS,
  selectDefaultMicrophone,
  setStoredDeviceId,
} from '@/utils/audio-devices';
import { logger } from '@/utils/logger';
import { isMac, isMobileDevice, isWindows } from '@/utils/platform';

// Re-export platform detection for backwards compatibility
export { isMac, isMobileDevice, isWindows } from '@/utils/platform';

export interface UseAudioDevicesOptions {
  /** Only enumerate devices after this is true (e.g., after user confirms) */
  enabled: boolean;
}

export interface UseAudioDevicesReturn {
  isMobile: boolean;
  loading: boolean;
  devicesLoaded: boolean;
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedMicrophone: string;
  selectedSpeaker: string;
  currentSpeakerLabel: string;
  supportsSetSinkId: boolean;
  selectMicrophone: (deviceId: string) => void;
  selectSpeaker: (deviceId: string) => void;
  testSpeaker: () => Promise<void>;
  /** Record the selected mic as last-known-good after a call that
   *  completed with the respondent heard (LUM-82). */
  rememberWorkingMicrophone: () => void;
  refreshDevices: () => Promise<void>;
  showMacBluetoothTip: boolean;
  dismissMacBluetoothTip: () => void;
}

export function useAudioDevices(
  options: UseAudioDevicesOptions,
): UseAudioDevicesReturn {
  const { enabled } = options;

  // Platform detection (computed once on mount)
  const [isMobile, setIsMobile] = useState(false);

  // Device state
  const [loading, setLoading] = useState(false);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('default');
  const [selectedSpeaker, setSelectedSpeaker] = useState('default');
  const [currentSpeakerLabel, setCurrentSpeakerLabel] = useState(
    'Detecting audio devices...',
  );

  // macOS Bluetooth tip state
  const [macBtTipDismissed, setMacBtTipDismissed] = useState(false);

  // Refs
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  /** The last-known-good mic seeds only the first enumeration. */
  const initialMicResolvedRef = useRef(false);

  // Browser capability detection
  const supportsSetSinkId = supportsSinkId();

  // Load persisted selections and check platform on mount
  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);

    if (mobile) {
      setDevicesLoaded(true);
      logger.debug('Mobile device detected, skipping device enumeration');
    }

    // Load persisted device selections
    const micId = getStoredDeviceId(STORAGE_KEYS.MICROPHONE);
    const spkId = getStoredDeviceId(STORAGE_KEYS.SPEAKER);
    const btTipDismissed =
      getStoredDeviceId(STORAGE_KEYS.MAC_BT_TIP_DISMISSED) === '1';

    if (micId) setSelectedMicrophone(micId);
    if (spkId) setSelectedSpeaker(spkId);
    setMacBtTipDismissed(btTipDismissed);
  }, []);

  // Fetch audio devices: enumerate + permission-state branches +
  // mobile/desktop split — extraction would fragment the closure.
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: device enumeration + permission states
  const fetchAudioDevices = useCallback(async () => {
    if (isMobile) return;

    setLoading(true);

    try {
      let hasPermission = false;

      // Request microphone permission for device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
          },
        });
        stream.getTracks().forEach(track => track.stop());
        hasPermission = true;
        logger.debug('Microphone permission granted, device labels available');
        void breadcrumb('mic_permission', { granted: true });
      } catch {
        logger.debug('Microphone permission denied, device labels unavailable');
        void breadcrumb('mic_permission', { granted: false });
      }

      // Enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      logger.debug('Available devices:', {
        total: devices.length,
        audioInputs: devices.filter(d => d.kind === 'audioinput').length,
        audioOutputs: devices.filter(d => d.kind === 'audiooutput').length,
        hasLabels: devices.some(d => d.label !== ''),
      });

      const mics = filterMicrophones(devices);
      const spks = filterSpeakers(devices);

      setMicrophones(mics);
      setSpeakers(spks);

      // Session default (LUM-82): the last mic a call completed on, if
      // it is plugged in — adopted as this session's pick so later
      // re-enumerations keep it. An unplugged one is skipped, not
      // forgotten; then the last pick, then the platform default.
      const lastGoodMic = initialMicResolvedRef.current
        ? null
        : getStoredDeviceId(STORAGE_KEYS.LAST_GOOD_MICROPHONE);
      initialMicResolvedRef.current = true;
      const persistedMic = getStoredDeviceId(STORAGE_KEYS.MICROPHONE);
      if (lastGoodMic && mics.some(m => m.deviceId === lastGoodMic)) {
        setSelectedMicrophone(lastGoodMic);
        setStoredDeviceId(STORAGE_KEYS.MICROPHONE, lastGoodMic);
      } else if (persistedMic && mics.some(m => m.deviceId === persistedMic)) {
        setSelectedMicrophone(persistedMic);
      } else if (mics.length > 0) {
        const chosenMic = selectDefaultMicrophone(
          mics,
          hasPermission,
          selectedMicrophone,
        );
        if (chosenMic) {
          setSelectedMicrophone(chosenMic.deviceId);
        }
      }

      // Set speaker selection and label
      if (spks.length > 0) {
        const persistedSpk = getStoredDeviceId(STORAGE_KEYS.SPEAKER);
        const defaultSpeaker = findDefaultSpeaker(spks);

        if (persistedSpk && spks.some(s => s.deviceId === persistedSpk)) {
          setSelectedSpeaker(persistedSpk);
          const match = spks.find(s => s.deviceId === persistedSpk);
          if (match?.label) setCurrentSpeakerLabel(match.label);
        } else if (defaultSpeaker) {
          setSelectedSpeaker(defaultSpeaker.deviceId || 'default');
          setCurrentSpeakerLabel(
            getSpeakerLabel(defaultSpeaker, hasPermission),
          );
        }
      } else {
        setCurrentSpeakerLabel('No audio output detected');
      }

      setDevicesLoaded(true);
      void breadcrumb('device_enum_ready', {
        mic_count: mics.length,
        speaker_count: spks.length,
        has_labels: devices.some(d => d.label !== ''),
        is_windows: isWindows(),
      });
    } catch (err) {
      logger.error('Failed to enumerate devices', { error: err });
      setDevicesLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [isMobile, selectedMicrophone]);

  // Fetch devices when enabled and not mobile
  useEffect(() => {
    if (!enabled || isMobile) return;

    logger.debug('User confirmed details, fetching audio devices');
    void breadcrumb('device_enum_start');
    fetchAudioDevices();

    const handleDeviceChange = () => fetchAudioDevices();
    navigator.mediaDevices?.addEventListener(
      'devicechange',
      handleDeviceChange,
    );

    return () => {
      navigator.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [enabled, isMobile, fetchAudioDevices]);

  // Select microphone with persistence
  const selectMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
    setStoredDeviceId(STORAGE_KEYS.MICROPHONE, deviceId);
  }, []);

  // Select speaker with persistence
  const selectSpeaker = useCallback(
    (deviceId: string) => {
      setSelectedSpeaker(deviceId);
      setStoredDeviceId(STORAGE_KEYS.SPEAKER, deviceId);
      const match = speakers.find(s => s.deviceId === deviceId);
      if (match?.label) setCurrentSpeakerLabel(match.label);
    },
    [speakers],
  );

  // Test speaker with tone: WebAudio nodes, fallback path for browsers
  // without setSinkId, MediaStreamDestination capture, abort signal.
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: WebAudio orchestration
  const testSpeaker = useCallback(async () => {
    try {
      if (!testAudioRef.current) {
        testAudioRef.current = document.createElement('audio');
        testAudioRef.current.style.display = 'none';
        document.body.appendChild(testAudioRef.current);
      }

      const AudioCtx =
        window.AudioContext ||
        (window as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioCtx) return;

      const audioCtx = new AudioCtx();
      if (audioCtx.state === 'suspended') await audioCtx.resume();

      const oscillator = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      const dest = audioCtx.createMediaStreamDestination();

      oscillator.type = 'sine';
      oscillator.frequency.value = 440;
      gain.gain.setValueAtTime(0, audioCtx.currentTime);
      gain.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + 0.12);

      oscillator.connect(gain).connect(dest);
      oscillator.start();

      const stopAt = audioCtx.currentTime + 2.0;
      gain.gain.linearRampToValueAtTime(0.0, stopAt - 0.2);

      setTimeout(() => {
        try {
          oscillator.stop();
          audioCtx.close();
        } catch {
          // Ignore cleanup errors
        }
      }, 2000);

      const el = testAudioRef.current;
      el.muted = false;
      el.volume = 1;
      el.autoplay = true;
      el.srcObject = dest.stream as unknown as MediaStream;

      if (supportsSetSinkId) {
        try {
          await (
            el as HTMLAudioElement & {
              setSinkId: (id: string) => Promise<void>;
            }
          ).setSinkId(selectedSpeaker);
        } catch {
          // setSinkId not available or failed
        }
      }

      try {
        await el.play();
        logger.debug('Speaker test played (webaudio)', {
          deviceId: selectedSpeaker,
        });
      } catch (playErr) {
        logger.warn('Speaker test play() failed; trying WAV fallback', {
          error: playErr,
        });
        el.srcObject = null;
        el.src = generateTestToneWav();

        if (supportsSetSinkId) {
          try {
            await (
              el as HTMLAudioElement & {
                setSinkId: (id: string) => Promise<void>;
              }
            ).setSinkId(selectedSpeaker);
          } catch {
            // setSinkId failed
          }
        }
        await el.play();
        logger.debug('Speaker test played (wav-fallback)', {
          deviceId: selectedSpeaker,
        });
      }
    } catch (e) {
      logger.warn('Speaker test failed', { error: e });
    }
  }, [selectedSpeaker, supportsSetSinkId]);

  // Dismiss macOS Bluetooth tip
  const dismissMacBluetoothTip = useCallback(() => {
    setMacBtTipDismissed(true);
    setStoredDeviceId(STORAGE_KEYS.MAC_BT_TIP_DISMISSED, '1');
  }, []);

  const rememberWorkingMicrophone = useCallback(() => {
    // The browser "default" follows the OS; pinning it would replace a
    // real remembered device with whatever the OS picks next time.
    if (!selectedMicrophone || selectedMicrophone === 'default') return;
    setStoredDeviceId(STORAGE_KEYS.LAST_GOOD_MICROPHONE, selectedMicrophone);
  }, [selectedMicrophone]);

  // Compute whether to show macOS Bluetooth tip
  const showMacBluetoothTip = (() => {
    if (macBtTipDismissed) return false;
    if (typeof window === 'undefined') return false;
    if (!isMac() || isMobileDevice()) return false;

    const selectedMic = microphones.find(
      m => m.deviceId === selectedMicrophone,
    );
    const label = selectedMic?.label || '';
    return isProblemBluetoothDevice(label);
  })();

  return {
    isMobile,
    loading,
    devicesLoaded,
    microphones,
    speakers,
    selectedMicrophone,
    selectedSpeaker,
    currentSpeakerLabel,
    supportsSetSinkId,
    selectMicrophone,
    selectSpeaker,
    testSpeaker,
    rememberWorkingMicrophone,
    refreshDevices: fetchAudioDevices,
    showMacBluetoothTip,
    dismissMacBluetoothTip,
  };
}

export default useAudioDevices;
